import { useState } from 'react';
import { ArrowDown, ArrowUp, GripVertical } from 'lucide-react';

const SECTION_LABELS: Record<string, string> = {
  form: 'Lead form',
  benefits: 'Why us',
  steps: 'Process',
  proof: 'Proof',
  offer: 'Offer',
  faq: 'FAQ',
  final: 'Closing CTA',
  header: 'Header',
  hero: 'Hero',
  trust: 'Trust',
  problem: 'The problem',
  uvp: 'Why them',
  services: 'Services',
  process: 'How it works',
  reviews: 'Reviews',
  pricing: 'Pricing',
  cta: 'Closing CTA',
  'final-cta': 'Closing CTA',
  footer: 'Footer',
  announcement: 'Announcement',
  'before-after': 'Before and after',
  specs: 'Specs',
  'pricing-or-comparison': 'Pricing',
  'why-us': 'Why us',
  'service-area': 'Service area',
  testimonials: 'Reviews',
};

const sectionLabel = (name: string) => SECTION_LABELS[name] ?? name;

export default function SectionOrderList({
  order,
  onMove,
  hidden,
  onToggleHidden,
}: {
  order: string[];
  onMove: (from: number, to: number) => void;
  hidden?: Set<string>;
  onToggleHidden?: (name: string) => void;
}) {
  const [dragFrom, setDragFrom] = useState<number | null>(null);
  const [over, setOver] = useState<number | null>(null);
  const clear = () => {
    setDragFrom(null);
    setOver(null);
  };

  return (
    <ol className="mt-2 space-y-1">
      {order.map((name, i) => (
        <li
          key={name}
          draggable
          onDragStart={(e) => {
            e.dataTransfer.setData('text/plain', String(i));
            setDragFrom(i);
          }}
          onDragOver={(e) => {
            e.preventDefault();
            setOver(i);
          }}
          onDragLeave={() => setOver((o) => (o === i ? null : o))}
          onDrop={() => {
            if (dragFrom !== null && dragFrom !== i) onMove(dragFrom, i);
            clear();
          }}
          onDragEnd={clear}
          className={`flex cursor-grab items-center gap-2 rounded border border-border bg-card px-3 py-1.5 text-sm ${
            hidden?.has(name) ? 'opacity-50' : ''
          } ${over === i && dragFrom !== null ? 'ring-2 ring-coral' : ''} ${
            dragFrom === i ? 'opacity-50' : ''
          }`}
        >
          <GripVertical className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
          {onToggleHidden && (
            <input
              type="checkbox"
              draggable={false}
              aria-label={`Show ${sectionLabel(name)}`}
              checked={!hidden?.has(name)}
              onChange={() => onToggleHidden(name)}
            />
          )}
          <span className="w-5 tabular-nums text-xs text-muted-foreground">{i + 1}</span>
          <span className="flex-1">{sectionLabel(name)}</span>
          <button
            type="button"
            aria-label={`Move ${sectionLabel(name)} up`}
            disabled={i === 0}
            onClick={() => onMove(i, i - 1)}
            className="rounded p-1 hover:bg-accent disabled:opacity-30"
          >
            <ArrowUp className="h-3.5 w-3.5" />
          </button>
          <button
            type="button"
            aria-label={`Move ${sectionLabel(name)} down`}
            disabled={i === order.length - 1}
            onClick={() => onMove(i, i + 1)}
            className="rounded p-1 hover:bg-accent disabled:opacity-30"
          >
            <ArrowDown className="h-3.5 w-3.5" />
          </button>
        </li>
      ))}
    </ol>
  );
}
